import {
  Field,
  TextArea,
  DropdownMenu,
  IconButton,
  Icon,
  Tooltip,
  Button,
} from '@kontentino/ui';
import AIGenerateButton from 'app/modules/aiContent/components/AIGenerateButton';
import { FC, useState } from 'react';
import {
  IMAGE_COUNT_OPTIONS,
  ORIENTATION_OPTIONS,
  STYLE_OPTIONS,
  TYPE_OPTIONS,
  AI_IMAGE_GENERATION_TIPS,
  AI_IMAGE_EDIT_TIPS,
} from 'app/modules/aiContent/constants/aiImageSuggestions';
import { useTranslation } from 'react-i18next';
import { faImage } from '@fortawesome/pro-regular-svg-icons/faImage';
import AiImageSuggestionsLoading from './AiImageSuggestionsLoading';
import ImageEditQuickActions from './ImageEditQuickActions';
import AiImageSuggestionsGallery from './AiImageSuggestionsGallery';
import FileSourceInput from 'app/components/FileSourceInput';
import { useFileSource } from 'app/components/fileSourceInput/hooks/useFileSource';
import MediaLibraryModal from 'app/modules/mediaLibrary/MediaLibraryModal';

import { styleImages } from './images/styles';

import { faQuestion } from '@fortawesome/pro-regular-svg-icons/faQuestion';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { faArrowUp } from '@fortawesome/pro-regular-svg-icons/faArrowUp';
import PageTypeUtils from 'app/utils/pageType';
import Modal from 'components/shared/Modal';
import KeyCodes from 'constants/keyCodes';

export type SelectOption<T> = {
  value: T;
  label: string;
} | null;

export type ModelOptionsGroup = {
  label: string;
  options: { value: string; label: string }[];
};

type Props = {
  mode?: 'create' | 'edit';
  description: string;
  selectedStyle: SelectOption<string>;
  selectedType: SelectOption<string>;
  imageCount: SelectOption<number>;
  orientation: SelectOption<string>;
  referenceImages: string[];
  resultImages?: string[];
  fileSource: ReturnType<typeof useFileSource>;
  onDescriptionChange: (value: string) => void;
  onStyleChange: (value: SelectOption<string>) => void;
  onTypeChange: (value: SelectOption<string>) => void;
  onImageCountChange: (value: SelectOption<number>) => void;
  onOrientationChange: (value: SelectOption<string>) => void;
  onReferenceImagesChange: (urls: string[]) => void;
  onUseInPost?: (imageUrls: string[]) => void;
  onRemixImage?: (imageUrl: string | string[]) => void;
  onRemoveResultImage?: (imageUrl: string) => void;
  isLoading?: boolean;
  handleSubmit: () => void;
  page: {
    id: number;
    type: number;
  };
  selectedModel?: SelectOption<string>;
  onModelChange?: (value: SelectOption<string>) => void;
  modelOptions?: ModelOptionsGroup[];
  isModelLoading?: boolean;
  isSubmitting?: boolean;
};

export const MAX_FILES = 5;
export const MAX_PROMPT_LENGTH = 500;

const AiImageSuggestionsForm: FC<Props> = ({
  mode = 'create',
  description,
  selectedStyle,
  selectedType,
  imageCount,
  orientation,
  referenceImages,
  resultImages = [],
  fileSource,
  onDescriptionChange,
  onStyleChange,
  onTypeChange,
  onImageCountChange,
  onOrientationChange,
  onReferenceImagesChange,
  onUseInPost,
  onRemixImage,
  onRemoveResultImage,
  isLoading = false,
  handleSubmit,
  page,
  selectedModel,
  onModelChange,
  modelOptions = [],
  isModelLoading = false,
  isSubmitting = false,
}) => {
  const { t } = useTranslation();
  const isEditMode = mode === 'edit';
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === KeyCodes.Enter && !(e.ctrlKey || e.metaKey || e.shiftKey)) {
      e.preventDefault();
      setSelectedImages([]);
      handleSelectedImagesChange([]);

      handleSubmit();
    }
  };

  const handleSelectedImagesChange = (newSelectedImages: string[]) => {
    setSelectedImages(newSelectedImages);
  };

  const handleUseSelectedImages = async () => {
    if (selectedImages.length > 0 && onUseInPost) {
      await onUseInPost(selectedImages);
      setSelectedImages([]);
    }
  };

  const handleRemixSelectedImages = () => {
    if (selectedImages.length > 0 && onRemixImage) {
      onRemixImage(selectedImages);
      setSelectedImages([]);
      handleSelectedImagesChange([]);
      handleSubmit();
    }
  };

  const getRemixButtonText = () => {
    if (referenceImages.length === 0 && resultImages.length === 0) {
      return 'Generate';
    }
    if (selectedImages.length > 0) {
      return `Remix ${selectedImages.length} selected`;
    }
    if (resultImages.length > 0) {
      return `Remix all`;
    }
    return 'Remix';
  };

  const handleRemixClick = () => {
    if (selectedImages.length > 0) {
      handleRemixSelectedImages();
    } else {
      setSelectedImages([]);
      handleSelectedImagesChange([]);

      handleSubmit();
    }
  };

  if (isLoading) {
    return <AiImageSuggestionsLoading imageCount={imageCount?.value} />;
  }

  return (
    <div className="tw-flex tw-max-h-[85vh] tw-flex-col tw-gap-4 tw-overflow-y-auto">
      {resultImages.length > 0 && onUseInPost && (
        <Button
          variant="primary"
          size="medium"
          iconBefore={<Icon icon={faArrowUp} />}
          className="tw-absolute tw-bottom-[9.5rem] tw-right-1/2 tw-z-20 tw-translate-x-1/2"
          isLoading={isSubmitting}
          disabled={isSubmitting}
          onClick={() => {
            const hasSelectedImages = selectedImages.length > 0;
            if (hasSelectedImages) {
              handleUseSelectedImages();
            } else {
              onUseInPost(resultImages);
            }
          }}
          data-cy="ai-image-suggestions_gallery_use-in-post"
        >
          {selectedImages.length > 0
            ? `Use ${selectedImages.length} selected`
            : `Use all`}
        </Button>
      )}

      <div
        data-name="ai-image-suggestions_controls-prompt-input"
        className="tw-absolute tw-inset-x-10 tw-bottom-4 tw-z-10 tw-rounded-xl tw-border tw-border-grayscale-30 tw-bg-grayscale-5/80 tw-backdrop-blur-md"
      >
        <Field.Group>
          <div className="tw-relative">
            <TextArea
              data-cy="ai-image-suggestions_description"
              placeholder={
                isEditMode
                  ? t('aiImageSuggestions.editModePrompt', {
                      defaultValue:
                        'Click a quick action above or describe your change, e.g., "Change headline to Happy Birthday"',
                    })
                  : t(
                      referenceImages.length > 0 || resultImages.length > 0
                        ? 'aiImageSuggestions.editImagePrompt'
                        : 'aiImageSuggestions.createImagePrompt',
                    )
              }
              value={description}
              rows={2}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= MAX_PROMPT_LENGTH) {
                  onDescriptionChange(value);
                }
              }}
              onKeyDown={handleKeyDown}
              className="focus:!tw-ring-0 focus-visible:!tw-ring-0 hover:!tw-ring-0 tw-w-full tw-rounded-lg tw-border-none tw-bg-transparent tw-pl-12 !tw-shadow-none !tw-ring-transparent placeholder:tw-text-grayscale-140"
            />
            <div className="tw-absolute tw-bottom-1 tw-right-2 tw-text-xs">
              {description.length}/{MAX_PROMPT_LENGTH}
            </div>
            <div className="tw-absolute tw-left-2 tw-top-2 tw-flex tw-items-center tw-gap-2">
              <Tooltip
                content={t('aiImageSuggestions.tooltips.referenceImages')}
                delay={500}
              >
                <div className="tw-relative">
                  <FileSourceInput
                    disabled={referenceImages.length >= MAX_FILES}
                    variant="plus-button"
                    fileSourceController={fileSource}
                    dropzoneContentProps={{
                      side: 'bottom',
                      align: 'start',
                    }}
                    triggerButton={
                      <IconButton
                        icon={<Icon icon={faPlus} />}
                        variant="secondary"
                        size="small"
                        className="tw-rounded-full tw-border tw-border-grayscale-30 !tw-p-2"
                        data-cy="ai-image-suggestions_add-reference-image"
                      />
                    }
                  />
                </div>
              </Tooltip>
            </div>
          </div>
          <div className="tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-2 tw-pb-2 tw-pl-2">
            {isEditMode && (
              <ImageEditQuickActions
                onPrefill={(prefill) => {
                  onDescriptionChange(prefill.prompt);
                  setTimeout(() => {
                    const textarea =
                      document.querySelector<HTMLTextAreaElement>(
                        '[data-cy="ai-image-suggestions_description"]',
                      );
                    if (!textarea) return;
                    textarea.focus();
                    if (
                      prefill.selectionStart !== undefined &&
                      prefill.selectionEnd !== undefined
                    ) {
                      textarea.setSelectionRange(
                        prefill.selectionStart,
                        prefill.selectionEnd,
                      );
                    }
                  }, 0);
                }}
                fontUploadSlot={
                  <FileSourceInput
                    disabled={referenceImages.length >= MAX_FILES}
                    variant="plus-button"
                    fileSourceController={fileSource}
                    dropzoneContentProps={{
                      side: 'top',
                      align: 'start',
                    }}
                    triggerButton={
                      <Button
                        size="small"
                        variant="secondary"
                        className="tw-shrink-0 tw-border-dashed tw-border-purple-100 hover:!tw-bg-purple-10 hover:!tw-text-grayscale-180"
                        iconBefore={
                          <Icon icon={faImage} className="tw-text-purple-100" />
                        }
                        title={t('imageEdit.useFontFromImageTooltip', {
                          defaultValue:
                            'Upload an image showing the font style you want. The AI will re-render all text in that font while keeping the original wording, colors, and layout.',
                        })}
                        onClick={() => {
                          onDescriptionChange(
                            'Re-render all text in this image using the font style from the second reference image. Keep all original text content, colors, sizes, positions, and the rest of the design unchanged.',
                          );
                        }}
                        data-cy="image-edit-action-use-font-from-image"
                      >
                        {t('imageEdit.useFontFromImage', {
                          defaultValue: 'Use font from image',
                        })}
                      </Button>
                    }
                  />
                }
                isLoading={isLoading}
              />
            )}
            {!isEditMode && (
              <>
                <DropdownMenu.Root modal={false}>
                  <Tooltip
                    content={t('aiImageSuggestions.tooltips.format')}
                    delay={500}
                  >
                    <DropdownMenu.Trigger asChild>
                      <Button
                        variant="secondary"
                        size="small"
                        className="tw-inline-flex tw-min-w-0 tw-max-w-[150px] !tw-truncate tw-whitespace-nowrap tw-border tw-border-grayscale-30"
                        data-cy="ai-image-suggestions_format-dropdown"
                      >
                        <span className="tw-max-w-full tw-truncate">
                          {selectedType
                            ? selectedType.label
                            : t('aiImageSuggestions.type')}
                        </span>
                      </Button>
                    </DropdownMenu.Trigger>
                  </Tooltip>
                  <DropdownMenu.Content
                    className="tw-z-30 tw-max-h-[14rem] tw-min-w-[248px] tw-overflow-y-auto"
                    side="bottom"
                    align="start"
                  >
                    <DropdownMenu.Label>
                      {t('aiImageSuggestions.type')}
                    </DropdownMenu.Label>
                    {TYPE_OPTIONS.map((option) => (
                      <DropdownMenu.Item
                        key={option.value}
                        onSelect={() => onTypeChange(option)}
                        selected={selectedType?.value === option.value}
                        className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-3"
                      >
                        <span>{option.label}</span>
                      </DropdownMenu.Item>
                    ))}
                  </DropdownMenu.Content>
                </DropdownMenu.Root>

                <DropdownMenu.Root modal={false}>
                  <Tooltip
                    content={t('aiImageSuggestions.tooltips.style')}
                    delay={500}
                  >
                    <DropdownMenu.Trigger asChild>
                      <Button
                        variant="secondary"
                        size="small"
                        className="tw-inline-flex tw-min-w-0 tw-max-w-[150px] tw-truncate tw-whitespace-nowrap tw-border tw-border-grayscale-30"
                        data-cy="ai-image-suggestions_style-dropdown"
                      >
                        <span className="tw-max-w-full tw-truncate">
                          {selectedStyle
                            ? selectedStyle.label
                            : t('aiImageSuggestions.style')}
                        </span>
                      </Button>
                    </DropdownMenu.Trigger>
                  </Tooltip>
                  <DropdownMenu.Content
                    className="tw-z-30 tw-max-h-[14rem] tw-min-w-[248px] tw-gap-3 tw-overflow-y-auto"
                    side="bottom"
                    align="start"
                  >
                    <DropdownMenu.Label>
                      {t('aiImageSuggestions.style')}
                    </DropdownMenu.Label>
                    {STYLE_OPTIONS.map((group) => {
                      if (group && 'options' in group && group.options) {
                        return (
                          <div key={group.label}>
                            <DropdownMenu.Label className="tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-grayscale-100">
                              {group.label}
                            </DropdownMenu.Label>
                            {group.options.map((option) => (
                              <DropdownMenu.Item
                                key={option?.value}
                                onSelect={() => option && onStyleChange(option)}
                                selected={
                                  selectedStyle?.value === option?.value
                                }
                                className="tw-flex tw-h-24 tw-flex-shrink-0 tw-items-center tw-gap-3 tw-p-2"
                              >
                                <div className="tw-relative tw-h-20 tw-w-20 tw-overflow-hidden tw-rounded">
                                  <img
                                    src={
                                      option?.value
                                        ? styleImages[
                                            option.value as keyof typeof styleImages
                                          ]
                                        : ''
                                    }
                                    alt={option?.label || ''}
                                    className="tw-h-full tw-w-full tw-object-cover"
                                  />
                                </div>
                                <span>{option?.label}</span>
                              </DropdownMenu.Item>
                            ))}
                          </div>
                        );
                      }
                      return null;
                    })}
                  </DropdownMenu.Content>
                </DropdownMenu.Root>

                <DropdownMenu.Root modal={false}>
                  <Tooltip
                    content={t('aiImageSuggestions.tooltips.variants')}
                    delay={500}
                  >
                    <DropdownMenu.Trigger asChild>
                      <Button
                        variant="secondary"
                        size="small"
                        className="tw-inline-flex tw-min-w-0 tw-max-w-[150px] tw-truncate tw-whitespace-nowrap tw-border tw-border-grayscale-30"
                        data-cy="ai-image-suggestions_variants-dropdown"
                      >
                        <span className="tw-max-w-full tw-truncate">
                          {imageCount ? imageCount.label : 'Variants'}
                        </span>
                      </Button>
                    </DropdownMenu.Trigger>
                  </Tooltip>
                  <DropdownMenu.Content
                    className="tw-z-30 tw-max-h-64 tw-min-w-[248px] tw-overflow-y-auto"
                    side="bottom"
                    align="start"
                  >
                    <DropdownMenu.Label>Variants</DropdownMenu.Label>
                    {IMAGE_COUNT_OPTIONS.map((option) => (
                      <DropdownMenu.Item
                        key={option.value}
                        onSelect={() => onImageCountChange(option)}
                        selected={imageCount?.value === option.value}
                        className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-3"
                      >
                        <span>{option.label}</span>
                      </DropdownMenu.Item>
                    ))}
                  </DropdownMenu.Content>
                </DropdownMenu.Root>

                <DropdownMenu.Root modal={false}>
                  <Tooltip
                    content={t('aiImageSuggestions.tooltips.aspectRatio')}
                    delay={500}
                  >
                    <DropdownMenu.Trigger asChild>
                      <Button
                        variant={orientation ? 'secondary' : 'ghost'}
                        size="small"
                        className="tw-inline-flex tw-min-w-0 tw-max-w-[150px] tw-truncate tw-whitespace-nowrap tw-border tw-border-grayscale-30"
                        data-cy="ai-image-suggestions_aspect-ratio-dropdown"
                      >
                        <span className="tw-max-w-full tw-truncate">
                          {orientation ? orientation.label : 'Aspect Ratio'}
                        </span>
                      </Button>
                    </DropdownMenu.Trigger>
                  </Tooltip>
                  <DropdownMenu.Content
                    className="tw-z-30 tw-max-h-64 tw-min-w-[248px] tw-overflow-y-auto"
                    side="bottom"
                    align="start"
                  >
                    <DropdownMenu.Label>
                      Aspect Ratio ({PageTypeUtils.getLabel(page.type)})
                    </DropdownMenu.Label>
                    {(ORIENTATION_OPTIONS[page.type] ?? []).map((option) => (
                      <DropdownMenu.Item
                        key={option.value}
                        onSelect={() => onOrientationChange(option)}
                        selected={orientation?.value === option.value}
                        className="tw-flex tw-items-center tw-gap-3"
                      >
                        <div
                          className="tw-border tw-border-grayscale-120"
                          style={{
                            width: '14px',
                            aspectRatio: option.value.replace(':', '/'),
                          }}
                        />
                        <span>{option.label}</span>
                      </DropdownMenu.Item>
                    ))}
                  </DropdownMenu.Content>
                </DropdownMenu.Root>

                {(modelOptions.flatMap((g) => g.options).length > 1 ||
                  isModelLoading) &&
                  onModelChange && (
                    <DropdownMenu.Root modal={false}>
                      <Tooltip
                        content={t(
                          'aiImageSuggestions.tooltips.model',
                          'AI Model',
                        )}
                        delay={500}
                      >
                        <DropdownMenu.Trigger asChild disabled={isModelLoading}>
                          <Button
                            variant="secondary"
                            size="small"
                            className="tw-inline-flex tw-min-w-0 tw-max-w-[150px] tw-truncate tw-whitespace-nowrap tw-border tw-border-grayscale-30"
                            data-cy="ai-image-suggestions_model-dropdown"
                            disabled={isModelLoading}
                          >
                            <span className="tw-max-w-full tw-truncate">
                              {isModelLoading
                                ? t('loading')
                                : selectedModel
                                ? selectedModel.label
                                : t('aiImageSuggestions.model', 'Model')}
                            </span>
                          </Button>
                        </DropdownMenu.Trigger>
                      </Tooltip>
                      <DropdownMenu.Content
                        className="tw-z-30 tw-max-h-64 tw-min-w-[248px] tw-overflow-y-auto"
                        side="bottom"
                        align="start"
                      >
                        {modelOptions.map((group) => (
                          <div key={group.label}>
                            <DropdownMenu.Label className="tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-grayscale-100">
                              {group.label}
                            </DropdownMenu.Label>
                            {group.options.map((option) => (
                              <DropdownMenu.Item
                                key={option.value}
                                onSelect={() =>
                                  onModelChange({
                                    value: option.value,
                                    label: option.label,
                                  })
                                }
                                selected={selectedModel?.value === option.value}
                                className="tw-flex tw-items-center tw-gap-3"
                              >
                                <span>{option.label}</span>
                              </DropdownMenu.Item>
                            ))}
                          </div>
                        ))}
                      </DropdownMenu.Content>
                    </DropdownMenu.Root>
                  )}
              </>
            )}

            <Tooltip
              content={t('aiImageSuggestions.tooltips.helpAndTips')}
              delay={500}
            >
              <IconButton
                icon={<Icon icon={faQuestion} />}
                variant="secondary"
                size="small"
                onClick={() => setIsHelpModalOpen(true)}
                aria-label="Help"
                className="tw-border tw-border-grayscale-30 tw-opacity-70"
                data-cy="ai-image-suggestions_help-button"
              />
            </Tooltip>

            <div className="tw-ml-auto tw-pr-2">
              <AIGenerateButton
                dataName="ai-image-suggestions_generate-images"
                onClick={handleRemixClick}
                isLoading={isLoading}
              >
                {getRemixButtonText()}
              </AIGenerateButton>
            </div>
          </div>
        </Field.Group>
      </div>
      <div className="tw-pb-48">
        <AiImageSuggestionsGallery
          runningUploads={fileSource.mediaUploader.runningUploads}
          referenceImages={referenceImages}
          resultImages={resultImages}
          onReferenceImagesChange={onReferenceImagesChange}
          onRemoveResultImage={onRemoveResultImage}
          onSelectedImagesChange={handleSelectedImagesChange}
        />
      </div>

      <MediaLibraryModal
        limits={{
          selectCount: MAX_FILES - referenceImages.length,
          allowedFiles: ['image'],
        }}
        page={page}
        isShown={fileSource.mediaLibrary.isShown}
        onHide={fileSource.mediaLibrary.hide}
        onSelectSubmit={(items) => {
          const urls = items
            .map((item) =>
              item.type === 'image' ? item.data.original.src : '',
            )
            .filter(Boolean);
          onReferenceImagesChange([...referenceImages, ...urls]);
        }}
      />

      <Modal
        open={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
        size="lg"
      >
        <Modal.Header
          title={
            isEditMode
              ? AI_IMAGE_EDIT_TIPS.title
              : AI_IMAGE_GENERATION_TIPS.title
          }
        />
        <Modal.Content>
          <div className="tw-space-y-6">
            {(isEditMode
              ? AI_IMAGE_EDIT_TIPS.categories
              : AI_IMAGE_GENERATION_TIPS.categories
            ).map((category) => (
              <div key={category.category}>
                <h3 className="tw-mb-3 tw-text-lg tw-font-semibold">
                  {category.category}
                </h3>
                <div className="tw-space-y-3">
                  {category.tips.map((tip, index) => (
                    <div
                      key={index}
                      className="tw-flex tw-items-start tw-gap-3"
                    >
                      <div
                        className={`tw-mt-2 tw-h-2 tw-w-2 tw-flex-shrink-0 tw-rounded-full ${category.colorClass}`}
                      />
                      <div>
                        <strong>{tip.split(':')[0]}:</strong>{' '}
                        {tip.split(':').slice(1).join(':').trim()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Modal.Content>
        <Modal.Footer withDivider>
          <Button variant="primary" onClick={() => setIsHelpModalOpen(false)}>
            Got it!
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AiImageSuggestionsForm;
